import { useState, useEffect } from "react";
import { PortalLayout } from "@/components/layout/PortalLayout";
import { useAuth } from "@/components/auth/AuthContext";
import { apiFetch } from "@/lib/apiFetch";
import { useQueryClient } from "@tanstack/react-query";

import { useToast } from "@/hooks/use-toast";
import { User, Lock, Trash2, Loader2, Save, AlertTriangle, Globe, Activity, Share2, Copy, Check, Star } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { COUNTRIES, countryFlag } from "@/lib/countries";
import { differenceInDays, format } from "date-fns";

const DUTY_OPTIONS = [
  { value: "active",     label: "Active",     color: "bg-green-500/20 text-green-400 border-green-500/40" },
  { value: "available",  label: "Available",  color: "bg-amber-500/20 text-amber-400 border-amber-500/40" },
  { value: "on-leave",   label: "On Leave",   color: "bg-blue-500/20 text-blue-400 border-blue-500/40" },
  { value: "mia",        label: "MIA",        color: "bg-red-500/20 text-red-400 border-red-500/40" },
];

function getServiceBadge(createdAt: string | null | undefined) {
  if (!createdAt) return { label: "Recruit", icon: "●", color: "text-muted-foreground" };
  const d = new Date(createdAt);
  if (isNaN(d.getTime())) return { label: "Recruit", icon: "●", color: "text-muted-foreground" };
  const days = differenceInDays(new Date(), d);
  if (days >= 730) return { label: "2-Year Elite", icon: "★★", color: "text-accent" };
  if (days >= 365) return { label: "1-Year Veteran", icon: "★", color: "text-yellow-400" };
  if (days >= 180) return { label: "6-Month Operator", icon: "◆", color: "text-blue-400" };
  if (days >= 30) return { label: "30-Day Operator", icon: "▲", color: "text-primary" };
  return { label: "Recruit", icon: "●", color: "text-muted-foreground" };
}

export default function Profile() {
  useSEO({ title: "My Profile" });
  const { user } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();

  const [bio, setBio] = useState(user?.bio ?? "");
  const [discordTag, setDiscordTag] = useState(user?.discordTag ?? "");
  const [nationality, setNationality] = useState((user as any)?.nationality ?? "");
  const [steamProfileUrl, setSteamProfileUrl] = useState((user as any)?.steam_profile_url ?? "");
  const [xboxGamertag, setXboxGamertag] = useState((user as any)?.xbox_gamertag ?? "");
  const [psnId, setPsnId] = useState((user as any)?.psn_id ?? "");
  const [savingProfile, setSavingProfile] = useState(false);
  const APPROVED_GAMES = ['Arma 3','Arma Reforger','Squad','Ready Or Not','Escape From Tarkov','Ground Branch','DayZ','Grey Zone Warfare','Body Cam','Operator','Exfil','Hell Let Loose'];
  const SME_TAGS_OPTIONS = ['Squad Leader','Platoon Commander','Company Commander','Sniper','Breacher','Medic','Combat Controller','Forward Observer','Drone Operator','Machine Gunner','Logistics','Intelligence Analyst','Signals','Artillery','Sapper/Engineer','Pilot','Vehicle Commander','Anti-Tank','JTAC','Instructor','CBRN','EOD'];
  const [lookingForUnit, setLookingForUnit] = useState<boolean>((user as any)?.looking_for_unit ?? false);
  const [operatorPublic, setOperatorPublic] = useState<boolean>((user as any)?.operator_profile_public ?? false);
  const [smeTags, setSmeTags] = useState<string[]>((user as any)?.sme_tags ?? []);
  const [preferredRoles, setPreferredRoles] = useState<string[]>((user as any)?.preferred_roles ?? []);
  const [preferredGames, setPreferredGames] = useState<string[]>((user as any)?.preferred_games ?? []);
  const [yearsExperience, setYearsExperience] = useState<string>(String((user as any)?.years_experience ?? ""));
  const [availability, setAvailability] = useState<string>((user as any)?.availability ?? "");

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);

  const [deletePassword, setDeletePassword] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const dutyStatus = (user as any)?.on_duty_status ?? "available";
  const currentDuty = DUTY_OPTIONS.find(d => d.value === dutyStatus) ?? DUTY_OPTIONS[0];

  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [recruits, setRecruits] = useState<any[]>([]);
  const [generatingCode, setGeneratingCode] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    apiFetch<{ referral_code: string | null }>("/users?path=referral-code/mine")
      .then(r => setReferralCode(r.referral_code)).catch(() => {});
    apiFetch<any[]>("/users?path=referral-code/recruits")
      .then(setRecruits).catch(() => {});
  }, []);

  if (!user) return null;

  const _createdAt = user.createdAt ?? user.created_at ?? null;
  const badge = getServiceBadge(_createdAt);
  const daysIn = _createdAt && !isNaN(new Date(_createdAt).getTime()) ? differenceInDays(new Date(), new Date(_createdAt)) : 0;

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await apiFetch("/authUpdateProfile?path=profile", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ bio, discordTag, nationality: nationality || null, steamProfileUrl: steamProfileUrl || null, xboxGamertag: xboxGamertag || null, psnId: psnId || null, looking_for_unit: lookingForUnit, operator_profile_public: operatorPublic, sme_tags: smeTags, preferred_roles: preferredRoles, preferred_games: preferredGames, years_experience: yearsExperience ? parseInt(yearsExperience) : null, availability: availability || null }),
      });
      qc.invalidateQueries({ queryKey: ["me"] });
      toast({ title: "Profile Updated", description: "Your profile has been saved." });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setSavingProfile(false);
    }
  };

  const changePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast({ title: "Error", description: "New passwords don't match.", variant: "destructive" });
      return;
    }
    setSavingPassword(true);
    try {
      await apiFetch("/authUpdateProfile?path=password", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      toast({ title: "Password Changed", description: "Your new password is active." });
      setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setSavingPassword(false);
    }
  };


  const generateReferralCode = async () => {
    setGeneratingCode(true);
    try {
      const r = await apiFetch<{ referral_code: string }>("/users?path=referral-code/generate", { method: "PATCH" });
      setReferralCode(r.referral_code);
      toast({ title: "Code Generated", description: "Your referral code is ready to share." });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setGeneratingCode(false);
    }
  };

  const copyCode = () => {
    if (referralCode) {
      navigator.clipboard.writeText(referralCode).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await apiFetch("/authUpdateProfile?path=account", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: deletePassword }),
      });
      qc.clear();
      window.location.href = "/";
    } catch (e: any) {
      toast({ title: "Deletion Failed", description: e.message, variant: "destructive" });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <PortalLayout>
      <div className="max-w-2xl mx-auto space-y-8">

        <div>
          <h1 className="font-display font-bold text-2xl uppercase tracking-widest">My Profile</h1>
          <p className="text-muted-foreground text-sm mt-1">Manage your callsign details, password, and account.</p>
        </div>

        {/* Service Record */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <Star className="w-5 h-5 text-primary" />
            <h2 className="font-display font-bold uppercase tracking-widest">Service Record</h2>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-1">Callsign</p>
              <p className="font-display font-bold text-foreground">{user.username}</p>
            </div>
            <div>
              <p className="text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-1">Clearance</p>
              <p className="font-display font-bold text-foreground uppercase">{user.role}</p>
            </div>
            <div>
              <p className="text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-1">Enlisted</p>
              <p className="font-display font-bold">{_createdAt && !isNaN(new Date(_createdAt).getTime()) ? format(new Date(_createdAt), "MMM dd, yyyy") : "—"}</p>
            </div>
            <div>
              <p className="text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-1">Service Badge</p>
              <p className={`font-display font-bold ${badge.color}`}>{badge.icon} {badge.label}</p>
              <p className="text-xs text-muted-foreground">{daysIn} days</p>
            </div>
          </div>
        </div>

        {/* Duty Status — auto-computed from activity */}
        <div className="bg-card border border-border rounded-lg p-6 space-y-3">
          <div className="flex items-center gap-3 mb-2">
            <Activity className="w-5 h-5 text-primary" />
            <h2 className="font-display font-bold uppercase tracking-widest">Duty Status</h2>
          </div>
          <div className={`flex items-center gap-3 px-4 py-3 border rounded font-display font-bold uppercase tracking-widest text-sm ${currentDuty.color}`}>
            <span className="w-2 h-2 rounded-full bg-current animate-pulse" />
            {currentDuty.label}
          </div>
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground font-sans">Based on AAR, Op & Training participation — not logins.</p>
            <span className="text-xs font-display font-bold text-primary">
              {(user as any).weekly_active_days ?? 0}/7 days this week
            </span>
          </div>
          {/* Weekly activity bar */}
          <div className="flex gap-1">
            {Array.from({ length: 7 }, (_, i) => {
              const weekly = (user as any).weekly_active_days ?? 0;
              return (
                <div key={i} className={`flex-1 h-2 rounded-sm transition-colors ${i < weekly ? "bg-primary" : "bg-secondary border border-border"}`} />
              );
            })}
          </div>
          <div className="text-xs text-muted-foreground font-sans space-y-1 pt-1 border-t border-border">
            <p><span className="text-green-400 font-bold">● Active</span> — 5–7 days/week participation</p>
            <p><span className="text-amber-400 font-bold">● Available</span> — 3–4 days/week</p>
            <p><span className="text-blue-400 font-bold">● On Leave</span> — 1–2 days/week</p>
            <p><span className="text-red-400 font-bold">● MIA</span> — no recorded activity this week</p>
          </div>
        </div>

        {/* Account Info */}
        <div className="bg-card border border-border rounded-lg p-6 space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-primary/20 border border-primary/40 rounded flex items-center justify-center">
              <User className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="font-display font-bold text-lg uppercase tracking-widest">{user.username}</p>
              <p className="text-xs text-muted-foreground">{user.email}</p>
            </div>
            <span className="ml-auto text-xs font-display font-bold uppercase tracking-widest px-2 py-1 border border-border rounded text-muted-foreground">{user.role}</span>
          </div>

          <div>
            <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-2">Bio</label>
            <textarea
              value={bio}
              onChange={e => setBio(e.target.value)}
              maxLength={500}
              rows={3}
              className="mf-input w-full resize-none"
              placeholder="A short bio about yourself..."
            />
            <p className="text-xs text-muted-foreground mt-1 text-right">{bio.length}/500</p>
          </div>

          <div>
            <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-2">Discord Tag</label>
            <input
              value={discordTag}
              onChange={e => setDiscordTag(e.target.value)}
              maxLength={50}
              className="mf-input w-full"
              placeholder="YourName#1234 or @yourname"
            />
          </div>

          <div>
            <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-2">
              <Globe className="inline w-3 h-3 mr-1" />Nationality
            </label>
            <div className="relative">
              <select
                value={nationality}
                onChange={e => setNationality(e.target.value)}
                className="mf-input w-full appearance-none pr-8"
              >
                <option value="">— Not set —</option>
                {COUNTRIES.map(c => (
                  <option key={c.code} value={c.code}>
                    {countryFlag(c.code)} {c.name}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">▾</div>
            </div>
          </div>

          {/* Platform Links */}
          <div className="pt-2 space-y-4 border-t border-border">
            <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">Platform Links</p>

            <div>
              <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-2">Steam Profile URL</label>
              <input
                value={steamProfileUrl}
                onChange={e => setSteamProfileUrl(e.target.value)}
                className="mf-input w-full"
                placeholder="https://steamcommunity.com/id/yourname"
              />
            </div>

            <div>
              <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-2">Xbox Gamertag</label>
              <input
                value={xboxGamertag}
                onChange={e => setXboxGamertag(e.target.value)}
                maxLength={60}
                className="mf-input w-full"
                placeholder="Your Xbox Gamertag"
              />
            </div>

            <div>
              <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-2">PlayStation Network ID</label>
              <input
                value={psnId}
                onChange={e => setPsnId(e.target.value)}
                maxLength={60}
                className="mf-input w-full"
                placeholder="Your PSN ID"
              />
            </div>
          </div>

          {/* ── Operator Profile ── */}
          <div className="pt-4 space-y-4 border-t border-border">
            <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">Operator Profile — Unit Search Visibility</p>

            <div className="flex items-center justify-between p-3 bg-secondary/30 border border-border rounded-lg">
              <div>
                <p className="text-sm font-display font-bold uppercase tracking-wider">Looking for Unit</p>
                <p className="text-xs text-muted-foreground">Commanders can discover and contact you</p>
              </div>
              <button onClick={() => setLookingForUnit(v => !v)} className={`w-12 h-6 rounded-full transition-colors relative ${lookingForUnit ? 'bg-primary' : 'bg-border'}`}>
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${lookingForUnit ? 'left-7' : 'left-1'}`} />
              </button>
            </div>

            <div className="flex items-center justify-between p-3 bg-secondary/30 border border-border rounded-lg">
              <div>
                <p className="text-sm font-display font-bold uppercase tracking-wider">Public Operator Profile</p>
                <p className="text-xs text-muted-foreground">Show your SME tags and availability on your public profile</p>
              </div>
              <button onClick={() => setOperatorPublic(v => !v)} className={`w-12 h-6 rounded-full transition-colors relative ${operatorPublic ? 'bg-primary' : 'bg-border'}`}>
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${operatorPublic ? 'left-7' : 'left-1'}`} />
              </button>
            </div>

            <div>
              <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-2">SME / Specialist Tags</label>
              <div className="flex flex-wrap gap-2">
                {SME_TAGS_OPTIONS.map(tag => (
                  <button key={tag} onClick={() => setSmeTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag])}
                    className={`px-2 py-1 rounded text-xs font-display font-bold uppercase tracking-wider border transition-colors ${smeTags.includes(tag) ? 'bg-primary text-primary-foreground border-primary' : 'bg-secondary border-border text-muted-foreground hover:border-primary/40'}`}>
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-2">Preferred Games</label>
              <div className="flex flex-wrap gap-2">
                {APPROVED_GAMES.map(g => (
                  <button key={g} onClick={() => setPreferredGames(prev => prev.includes(g) ? prev.filter(x => x !== g) : [...prev, g])}
                    className={`px-2 py-1 rounded text-xs font-display font-bold uppercase tracking-wider border transition-colors ${preferredGames.includes(g) ? 'bg-primary text-primary-foreground border-primary' : 'bg-secondary border-border text-muted-foreground hover:border-primary/40'}`}>
                    {g}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-2">Years of Milsim Experience</label>
                <input type="number" min="0" max="20" value={yearsExperience} onChange={e => setYearsExperience(e.target.value)} className="mf-input w-full" placeholder="0" />
              </div>
              <div>
                <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-2">Availability</label>
                <select value={availability} onChange={e => setAvailability(e.target.value)} className="mf-input w-full">
                  <option value="">— Not set —</option>
                  <option value="Weekends">Weekends only</option>
                  <option value="Evenings">Weekday evenings</option>
                  <option value="Flexible">Flexible</option>
                  <option value="Full-time">Full-time milsim</option>
                </select>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <button
              onClick={saveProfile}
              disabled={savingProfile}
              className="flex items-center gap-2 px-5 py-2 bg-primary hover:bg-primary/90 text-primary-foreground font-display font-bold uppercase tracking-wider text-sm rounded transition-all disabled:opacity-50"
            >
              {savingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Profile
            </button>
          </div>
        </div>

        {/* Referral Code */}
        <div className="bg-card border border-border rounded-lg p-6 space-y-4">
          <div className="flex items-center gap-3 mb-2">
            <Share2 className="w-5 h-5 text-primary" />
            <h2 className="font-display font-bold uppercase tracking-widest">Recruitment Code</h2>
          </div>
          <p className="text-xs text-muted-foreground font-sans">Share this code with potential recruits. When they register, they'll be linked to you.</p>
          {referralCode ? (
            <div className="flex items-center gap-3">
              <div className="flex-1 bg-secondary border border-border rounded px-4 py-3 font-mono font-bold text-primary tracking-widest text-sm">
                {referralCode}
              </div>
              <button
                onClick={copyCode}
                className="flex items-center gap-2 px-4 py-3 border border-border rounded font-display font-bold uppercase text-xs text-muted-foreground hover:text-foreground hover:border-primary/50 transition-all"
              >
                {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                {copied ? "Copied!" : "Copy"}
              </button>
            </div>
          ) : (
            <button
              onClick={generateReferralCode}
              disabled={generatingCode}
              className="flex items-center gap-2 px-5 py-2.5 bg-secondary border border-border hover:border-primary/50 text-foreground font-display font-bold uppercase tracking-wider text-sm rounded transition-all disabled:opacity-50"
            >
              {generatingCode ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
              Generate My Code
            </button>
          )}
          {recruits.length > 0 && (
            <div>
              <p className="text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-2">Recruited by You ({recruits.length})</p>
              <div className="space-y-1">
                {recruits.map((r: any) => (
                  <div key={r.id} className="flex items-center justify-between text-sm px-3 py-2 bg-secondary/40 rounded">
                    <span className="font-display font-bold text-foreground">{r.username}</span>
                    <span className="text-xs text-muted-foreground">{r.created_at && !isNaN(new Date(r.created_at).getTime()) ? format(new Date(r.created_at), "MMM dd, yyyy") : "—"}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Change Password */}
        <div className="bg-card border border-border rounded-lg p-6 space-y-4">
          <div className="flex items-center gap-3 mb-2">
            <Lock className="w-5 h-5 text-primary" />
            <h2 className="font-display font-bold uppercase tracking-widest">Change Password</h2>
          </div>

          <div>
            <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-2">Current Password</label>
            <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className="mf-input w-full" placeholder="••••••••" />
          </div>
          <div>
            <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-2">New Password</label>
            <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="mf-input w-full" placeholder="Min 8 characters" />
          </div>
          <div>
            <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-2">Confirm New Password</label>
            <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="mf-input w-full" placeholder="••••••••" />
          </div>

          <div className="flex justify-end pt-2">
            <button
              onClick={changePassword}
              disabled={savingPassword || !currentPassword || !newPassword || !confirmPassword}
              className="flex items-center gap-2 px-5 py-2 bg-primary hover:bg-primary/90 text-primary-foreground font-display font-bold uppercase tracking-wider text-sm rounded transition-all disabled:opacity-50"
            >
              {savingPassword ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              Change Password
            </button>
          </div>
        </div>

        {/* Delete Account */}
        <div className="bg-card border border-destructive/30 rounded-lg p-6 space-y-4">
          <div className="flex items-center gap-3 mb-2">
            <AlertTriangle className="w-5 h-5 text-destructive" />
            <h2 className="font-display font-bold uppercase tracking-widest text-destructive">Delete Account</h2>
          </div>
          <p className="text-sm text-muted-foreground">
            Permanently deletes your account and all associated data. This cannot be undone.
          </p>

          {!showDeleteConfirm ? (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="px-5 py-2 border border-destructive/50 text-destructive hover:bg-destructive/10 font-display font-bold uppercase tracking-wider text-sm rounded transition-all"
            >
              Delete My Account
            </button>
          ) : (
            <div className="space-y-3 p-4 bg-destructive/10 border border-destructive/30 rounded-lg">
              <p className="text-xs text-foreground font-medium">Enter your password to confirm deletion:</p>
              <input
                type="password"
                value={deletePassword}
                onChange={e => setDeletePassword(e.target.value)}
                className="mf-input w-full"
                placeholder="Your password"
              />
              <div className="flex gap-2">
                <button
                  onClick={deleteAccount}
                  disabled={deleting || !deletePassword}
                  className="flex items-center gap-2 px-4 py-2 bg-destructive hover:bg-destructive/90 text-white font-display font-bold uppercase text-xs rounded"
                >
                  {deleting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                  Confirm Delete
                </button>
                <button onClick={() => setShowDeleteConfirm(false)} className="px-4 py-2 border border-border rounded text-xs font-display uppercase text-muted-foreground hover:text-foreground">Cancel</button>
              </div>
            </div>
          )}
        </div>

      </div>
    </PortalLayout>
  );
}
